/**
 * A job's day sheets (GET /jobs/:id/sheets) and opening one
 * (POST /jobs/:id/sheets, fieldiq-api#1338).
 *
 * One sheet per unit per local work day. Opening names the unit (required
 * when the job has more than one), the day (default the tenant's today) and
 * optionally a crew template of that unit to copy. A 409 sheet_exists links to
 * the sheet already open; future_day, unit_not_on_job and job_not_scheduled
 * are shown as the API's sentences.
 *
 * Hours, crew counts and footage are the API's.
 */
import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useWlUnit } from '../../hooks/useWirelineSlickline';
import { useOpenWlSheet, useWlJobSheets } from '../../hooks/useWlJobs';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { wlClockRange, wlDay, wlExistingSheetId, wlHours, wlJobRefusal, wlQty, wlSheetStatusSkin } from '../../lib/wireline-slickline-jobs';
import type { WlJobDetail, WlSheetSummary } from '../../types/wireline-slickline-jobs';
import { useOpenRecord } from '../downhole/recordPage';
import { WL_INPUT, WL_PRIMARY, WL_SECONDARY, WL_SMALL_BTN, WlBadge, WlDialog, WlEmpty, WlField, WlFormError, WlPanel, WlRegister, type WlColumn } from './WlBits';

export const wlSheetPath = (id: string) => `/wireline-slickline/sheets/${id}`;

export function JobSheetsPanel({ job, canOpen, today }: { job: WlJobDetail; canOpen: boolean; today: string | null }) {
  const openRecord = useOpenRecord();
  const navigate = useNavigate();
  const query = useWlJobSheets(job.id);
  const sheets = query.data?.data ?? [];
  const open = useOpenWlSheet();
  const [opening, setOpening] = useState(false);
  const [unitId, setUnitId] = useState(job.units.length === 1 ? job.units[0].unitId : '');
  const [day, setDay] = useState(today ?? '');
  const [templateId, setTemplateId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [existingId, setExistingId] = useState<string | null>(null);
  const unit = useWlUnit(opening && unitId ? unitId : undefined);
  const crews = unit.data?.data?.crews ?? [];
  const sheetable = job.status === 'scheduled' || job.status === 'in_progress';

  const columns: WlColumn<WlSheetSummary>[] = [
    { key: 'day', header: 'Day', render: (s) => <span className="font-semibold text-white">{wlDay(s.workDate)}</span> },
    { key: 'unit', header: 'Unit', render: (s) => s.unitNumber || '—' },
    { key: 'status', header: 'Status', render: (s) => <WlBadge skin={wlSheetStatusSkin(s.status, s.statusLabel)} value={s.status} /> },
    { key: 'window', header: 'Window', render: (s) => <span className="font-mono text-sm">{wlClockRange(s.startTime, s.endTime)}</span> },
    { key: 'hours', header: 'Hours', align: 'right', render: (s) => <span className="font-mono text-sm">{wlHours(s.sheetHours)}</span> },
    { key: 'crew', header: 'Crew', align: 'right', render: (s) => <span className="font-mono text-sm">{displayValue(s.crewCount)}</span> },
    { key: 'runs', header: 'Runs', align: 'right', render: (s) => <span className="font-mono text-sm">{displayValue(s.runCount)}</span> },
    { key: 'footage', header: 'Footage', align: 'right', render: (s) => <span className="font-mono text-sm">{wlQty(s.footage, 1)}</span> },
  ];

  return (
    <WlPanel
      eyebrow="Day sheets"
      title="Day sheets"
      testId="wl-job-sheets"
      actions={
        canOpen && sheetable && job.units.length > 0 ? (
          <button type="button" className={WL_SMALL_BTN} onClick={() => { setError(null); setExistingId(null); setOpening(true); }}>
            <Plus className="size-3.5" aria-hidden="true" />
            Open a day sheet
          </button>
        ) : undefined
      }
      footer={sheets.length > 0 ? `${sheets.length} sheet${sheets.length === 1 ? '' : 's'}` : undefined}
    >
      <WlRegister
        query={query}
        rows={sheets}
        columns={columns}
        rail={(s) => wlSheetStatusSkin(s.status).rail}
        onOpen={(s) => openRecord(wlSheetPath(s.id))}
        subject="the day sheets"
        testId="wl-job-sheet"
        minWidth="min-w-[860px]"
        empty={
          <WlEmpty>
            No day sheet yet.
            {!sheetable ? ' Sheets open once the job is scheduled.' : job.units.length === 0 ? ' Put a unit on the job first.' : ''}
          </WlEmpty>
        }
        card={(s) => (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-extrabold text-white">{wlDay(s.workDate)}</p>
              <WlBadge skin={wlSheetStatusSkin(s.status, s.statusLabel)} value={s.status} />
            </div>
            <p className="mt-1 text-xs text-fieldiq-muted">
              {s.unitNumber || '—'} · {wlClockRange(s.startTime, s.endTime)} · {wlHours(s.sheetHours)}
            </p>
            <p className="mt-1 text-xs text-fieldiq-text">
              {displayValue(s.crewCount)} crew · {displayValue(s.runCount)} run{s.runCount === 1 ? '' : 's'} · {wlQty(s.footage, 1)}
            </p>
          </>
        )}
      />
      <WlDialog
        open={opening}
        title="Open a day sheet"
        onClose={() => setOpening(false)}
        footer={
          <>
            <button type="button" className={WL_SECONDARY} onClick={() => setOpening(false)} disabled={open.isPending}>
              Cancel
            </button>
            <button
              type="button"
              className={WL_PRIMARY}
              disabled={!unitId || open.isPending}
              onClick={() => {
                setError(null);
                setExistingId(null);
                open.mutate(
                  { jobId: job.id, body: { unit_id: unitId, ...(day ? { work_date: day } : {}), ...(templateId ? { crew_template_id: templateId } : {}) } },
                  {
                    onSuccess: (res) => {
                      toast.success('Day sheet opened');
                      setOpening(false);
                      if (res.data?.id) navigate(wlSheetPath(res.data.id));
                    },
                    onError: (err) => {
                      setError(wlJobRefusal(err, 'The day sheet could not be opened.'));
                      setExistingId(wlExistingSheetId(err));
                    },
                  },
                );
              }}
            >
              {open.isPending ? 'Opening…' : 'Open sheet'}
            </button>
          </>
        }
      >
        <div className="space-y-3">
          <WlFormError message={error} />
          {existingId && (
            <Link to={wlSheetPath(existingId)} className="text-sm font-semibold text-fi-amber underline" data-testid="wl-existing-sheet">
              Open the sheet already there
            </Link>
          )}
          <WlField label="Unit">
            <select className={WL_INPUT} aria-label="Sheet unit" value={unitId} onChange={(e) => { setUnitId(e.target.value); setTemplateId(''); }}>
              <option value="">Choose a unit</option>
              {job.units.map((u) => (
                <option key={u.unitId} value={u.unitId}>
                  {u.unitNumber || '—'}
                </option>
              ))}
            </select>
          </WlField>
          <WlField label="Work day" hint="The tenant’s local day; a future day is refused">
            <input type="date" className={WL_INPUT} aria-label="Work day" value={day} onChange={(e) => setDay(e.target.value)} />
          </WlField>
          <WlField label="Copy crew from" hint="The unit’s crew templates; change the crew on the sheet after">
            <select className={WL_INPUT} aria-label="Copy crew from" value={templateId} onChange={(e) => setTemplateId(e.target.value)} disabled={!unitId}>
              <option value="">{unit.isLoading ? 'Loading…' : crews.length ? 'Do not copy a crew' : 'The unit has no crew template'}</option>
              {crews.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} · {c.members.length} {c.members.length === 1 ? 'hand' : 'hands'}
                </option>
              ))}
            </select>
          </WlField>
        </div>
      </WlDialog>
    </WlPanel>
  );
}
